<template>
  <div class="fields-module">
    <div
      v-if="showNextTemplateButton || selectedInstanceIndex"
      class="d-flex justify-content-between mb-3"
    >
      <b-button-group size="sm">
        <b-button
          v-if="showNextTemplateButton"
          variant="outline-primary"
          @click="moveInstanceSelection(1)"
        >
          <b-icon icon="arrow-down" />
          Next template
        </b-button>
        <b-button
          v-if="selectedInstanceIndex"
          variant="outline-primary"
          @click="moveInstanceSelection(-1)"
        >
          Prev template
          <b-icon icon="arrow-up" />
        </b-button>
      </b-button-group>
      <span>{{ totalTemplateOrder }}/{{ totalTemplatesCount }}</span>
    </div>
    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'translateFieldsSelection')"
      >
        <div class="d-flex align-items-center">
          <i class="bi bi-translate green-fonts" />
          <strong> Translate</strong>
        </div>
        <b-icon
          :icon="
            isOpen.translateFieldsSelection ? 'chevron-up' : 'chevron-down'
          "
        />
      </div>
      <transition>
        <b-collapse
          :visible="isOpen.translateFieldsSelection"
        >
          <div>
            <b-form-group v-if="textFields.length === 0">
              This template has no text fields
            </b-form-group>
            <b-form-group
              v-if="textFields.length > 0"
              class="translation-form-group mb-0"
            >
              <div class="d-flex flex-column">
                <Multiselect
                  v-model="translateTargetLanguages"
                  class="flex-grow-1 form-control px-1 py-0 font-base flex items-center ms-bootstrap-size"
                  style="width: auto !important; height: auto !important;"
                  :options="sortedLanguages"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  placeholder="Choose language(s)..."
                />
                <b-button
                  v-if="translateTargetLanguages.length <= 1"
                  class="flex-shrink-0 w-100 mt-2"
                  @click="translate"
                >
                  Translate
                </b-button>
                <b-button
                  class="flex-shrink-0 w-100 mt-2"
                  @click="copyAndTranslate"
                >
                  <span>Copy Template & Translate</span>
                  <b-badge
                    v-if="translateTargetLanguages.length > 1"
                    variant="dark"
                    class="ml-2"
                  >
                    {{ translateTargetLanguages.length }}
                  </b-badge>
                </b-button>
                <b-button
                  v-if="translateTargetLanguages.length <= 1"
                  class="flex-shrink-0 w-100 mt-2"
                  @click="copyFieldsAndTranslate"
                >
                  Copy Fields & Translate
                </b-button>
              </div>
            </b-form-group>
            <TranslateImageButton :target-languages="translateTargetLanguages" />
          </div>
        </b-collapse>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { editorControlEventBroker, EVENTS } from '@frontend/group/modules/editor-control-popup/event-broker'
import { setSelectedInstance } from '@frontend/group/modules/copy-paste-object'
import { HANDLER_TYPES } from "@frontend/group/modules/translate-template";
import {GLOBAL_UPDATE_LOCAL_STORAGE_KEY, isGlobalUpdateOn} from "@frontend/constants/global-update";
import { LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_TEXT,
  LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_BG_COLOR,
 } from "@frontend/group/modules/editor-control-popup/selected-template-for-apply-to-all";
 import Multiselect from 'vue-multiselect';
 import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import SelectAllObjectsButton
  from '@frontend/components/modules/layouts-page/EditorControl/components/SelectAllObjectsButton.vue'
import TranslateImageButton
  from '@frontend/components/modules/layouts-page/EditorControl/components/TranslateImageButton.vue'
import {
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID,
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
 } from '@frontend/constants/templates-field-prefixes';

const getShowPageScope = () => ShowPageScopeCollector.get([
  'previews',
  'saveLayout',
]);

const BG_THEME_PREFIXES = [
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID
];

export default {
  name: 'TranslateModule',
  components: {
    Multiselect,
    TranslateImageButton
  },
  props: {
    isOpen: {
      type: Object,
      default: () => ({
        textEditorFieldsSection: true,
        translateFieldsSelection: true,
        backgroundFieldsSection: true,
        productImagesFieldsSection: true,
        imagesFieldSection: true
      }),
    },
    options: {
      type: Object,
      default: () => {},
    },
    target: {
      type: Object,
      default: () => null,
      required: false,
    },
    defaultColor: {
      type: String,
      default: "#ffffff",
      required: false
    },
  },
  data: () => ({
    translateTargetLanguages: [],
    languages: [
      'Arabic',
      'Bengali',
      'Cantonese',
      'Czech',
      'Dutch',
      'English',
      'French-CA',
      'French-FR',
      'German',
      'Greek',
      'Hebrew',
      'Hindi',
      'Italian',
      'Japanese',
      'Korean',
      'Polish',
      'Portuguese',
      'Russian',
      'Simplified Chinese',
      'Slovak',
      'Spanish (LatAm)',
      'Spanish (Spain/EU)',
      'Swedish',
      'Thai',
      'Traditional Chinese',
      'Turkish',
      'Vietnamese'
    ],
    loadingCopyFieldsAndTranslate: false,
    loadingCopyAndTranslate: false,
    loadingTranslate: false,
    isApplyToAllGlobalUpdate: false,
    editor: {},
    LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_TEXT,
    LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_BG_COLOR,
    backgroundProperties: {
      selectedField: null,
    },
    fields: [],
  }),

  computed: {
    ...mapGetters('layout', [
      'layers',
      'allProductImages',
      'selectedInstance',
      'selectedInstanceIndex',
      'selectedInstanceFieldsWithType',
      'selectedInstanceAdditionalFields',
      'selectedInstanceTextBoxFields',
      'currentPagePreviews',
    ]),
    textFields () {
      return this.selectedInstanceTextBoxFields.filter(field => field.visible === true);
    },
    multilineTextFields () {
      return this.selectedInstanceFieldsWithType('Multi Line Text')
    },
    additionalTextFields () {
      return this.selectedInstanceAdditionalFields.filter(field => field.type === 'Text');
    },
    additionalMultilineTextFields () {
      return this.selectedInstanceAdditionalFields.filter(field => field.type === 'Multi Line Text');
    },
    showNextTemplateButton () {
      return !!this.currentPagePreviews.at(this.selectedInstanceIndex + 1)
    },
    currentPage() {
      return +$('input[name="current_page"]').val()
    },
    perPage() {
      return +$('input[name="per_page"]').val()
    },
    totalTemplateOrder () {
      return this.selectedInstanceIndex + 1 + (this.currentPage - 1) * this.perPage
    },
    totalTemplatesCount () {
      return window.preview_data.searchTemplateDropdownItems.length
    },
    sortedLanguages() {
      return [...(this.languages || [])].sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
      );
    }
  },
  watch: {
    target() {
      this.initEditor();
    }
  },
  mounted() {
    this.isApplyToAllGlobalUpdate = isGlobalUpdateOn();
    if (localStorage.getItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY) === null) {
      localStorage.setItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY, false);
    }
    editorControlEventBroker.on(EVENTS.GLOBAL_UPDATE, () => {
      this.isApplyToAllGlobalUpdate = !this.isApplyToAllGlobalUpdate;
    });
    this.initEditor();
  },

  methods: {
    initEditor() {
      this.editor = this.target?.editor;
      if (this.editor?.colors?.length === 0) {
        this.editor.colors = [this.defaultColor];
      }
    },
    moveInstanceSelection(step) {
      const newSelectedInstance = this.currentPagePreviews[this.selectedInstanceIndex + step]
      newSelectedInstance.canvas.wrapperEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setSelectedInstance(newSelectedInstance.id)
    },
    async translate() {
      const image = await this.captureCanvas();
      this.emitTranslateEvent(HANDLER_TYPES.TRANSLATE, image);
    },
    async copyAndTranslate() {
      const image = await this.captureCanvas();
      this.emitTranslateEvent(HANDLER_TYPES.COPY_AND_TRANSLATE, image);
    },
    async copyFieldsAndTranslate() {
      const image = await this.captureCanvas();
      this.emitTranslateEvent(HANDLER_TYPES.COPY_FIELDS_AND_TRANSLATE, image);
    },
    async captureCanvas() {
      if (!this.selectedInstance || !this.selectedInstance.canvas) {
        return null;
      }
      return this.selectedInstance.canvas.toDataURL({
        format: 'png',
        quality: 0.8,
        multiplier: 0.5,
      });
    },
    handleRTLLanguages(lang) {
      const rtlLanguages = ['Arabic', 'Hebrew'];
      const isRTL = Array.isArray(lang)
        ? lang.some(l => rtlLanguages.includes(l))
        : rtlLanguages.includes(lang);
      
      if (isRTL) {
        const canvas = this.selectedInstance?.canvas;
        if (!canvas) return;

        const selectedObjects = canvas.getActiveObjects();
        selectedObjects.forEach(obj => {
          if (obj.type === 'textbox' || obj.type === 'i-text' || obj.isEditingText) {
            obj.set({ textAlign: 'right' });
            if (obj.setCoords) obj.setCoords();
          }
        });

        canvas.requestRenderAll();
      }
    },
    async emitTranslateEvent(handler, image = null) {
      if (!Array.isArray(this.translateTargetLanguages) || this.translateTargetLanguages.length === 0) {
        return;
      }

      const { saveLayout } = getShowPageScope();
      await saveLayout(true, false, 'Translating...');

      const lang =
        this.translateTargetLanguages.length === 1
          ? this.translateTargetLanguages[0]
          : [...this.translateTargetLanguages];

      editorControlEventBroker.fire(EVENTS.TRANSLATE, {
        textFields: this.textFields,
        multilineTextFields: this.multilineTextFields,
        additionalTextFields: this.additionalTextFields,
        additionalMultilineTextFields: this.additionalMultilineTextFields,
        instance: this.selectedInstance,
        lang,
        handler,
        image
      });

      setTimeout(() => {
        this.handleRTLLanguages(lang);
      }, 2000);
    }
  }
}
</script>

<style scoped lang="scss">
.translation-form-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
    ::v-deep {
        ul.dropdown-menu {
            width: 100%;
        }
    }
}

  .button-wrapper {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  }

  button {
    white-space: nowrap;
  }

  .maintain-button-width {
    min-width: max-content;
  }
  .colors-item {
    width: 100%;
    padding-bottom: 10px;
  }
  .colors-item input {
    // background-color: #f8f9fa !important;
    border-radius: 0.35rem !important;
    border-color: #e9ecef !important;
  }
  .multiselect {
    min-width: 80px;
  }
  .background-dropdown {
    .background-dropdown {
        margin-top: 10px;
      }
      .background-dropdown-label {
        width: 60px;
        height: 22px;
      }

      ::v-deep(.multiselect__select) {
        left: 50px !important;
        right: auto;
      }

      ::v-deep(.multiselect__single) {
        padding-left: 0 !important;
      }
  }
  
::v-deep(.background-dropdown .multiselect__content-wrapper) {
  width: 245px;
  box-sizing: border-box;
  left: -5px;
}

.multiselect__single .background-dropdown-label__image{
  max-width: 28px !important;
  padding-top: 35px !important;
}

.preview-img:hover .delete-overlay {
    display: block;
}
.cloud-overlay-icon {
  position: absolute;
  z-index: 10;
  top: 0;
  left: -15px;
  cursor: pointer;
}
.background-dropdown-label__image {
  margin-left: 2px;
}
.label-option {
  position: relative;
  margin: 0 5px;
}
.image-option {
  width: 30px;
  height: auto;
}
.label-option .close-btn {
  opacity: 0; 
  position: absolute;
  top: 1px;
  right: 2px;
  background-color: red;
  vertical-align: -.15em;
  color: white;
  border: none;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
  transition: opacity 0.3s ease-in-out;
}

.replace-image-btn i {
  color: #000;
}

.label-option .replace-image-btn {
  position: absolute;
  bottom: 0;
  left: 2px;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  border: 0;
  background: transparent;
  padding: 0;
  color: #209c86;
}

.label-option:hover .close-btn,
.label-option:hover .replace-image-btn {
  opacity: 1;
}

.dropdown-product-images {
  & input:hover { 
    cursor: pointer;
  }
}

.product-ids-input[readonly] {
    background: #fff;
}

.file-browser-wrapper {
    max-height: 770px;
    overflow-y: auto;
    ::v-deep {
        .c-main-view {
            border: none;
        }
    }
}

.buttons-wrapper {
  display: flex;
  gap: 10px;
  margin: 15px 10px 0 0;
  justify-content: end;
}
::v-deep {
  .products-container {
    width: calc(65vw - 9px);
    height: fit-content;
    position: fixed;
    background: rgb(255, 255, 255);
    z-index: 101;
    top: 87px;
    right: 64px;
    border: 1px solid;
    box-shadow: 2px 2px #888888;
    overflow-y: auto;
    padding: 20px;
  }
  
  .contract-btn-wrapper {
    display: flex;
    justify-content: space-between;
  }

  .contract-btn, .close-btn {
    cursor: pointer;
  }

  .products-container--contracted {
    width: 400px;
    padding: 5px 20px;
    
    .file-browser-wrapper {
      display: none;
    }
    
    .buttons-wrapper {
      display: none;
    }
  }
}

.replace-options-dropdown {
  .list-group {
    min-width: 300px;
    .list-group-item {
      &:first-child {
        border-top: 0;
      }
      &:last-child {
        border-bottom: 0;
      }
    }
    
    
  }
  .dropdown-menu.show {
    padding-top:0 !important;
    padding-bottom: 0 !important;
    border:0 !important;
  }
}

.add-icon {
  cursor: pointer;
}

.color-fill-item {
  height: 38px !important;
  padding: 7px 7px !important;
  width: 15% !important;
}

.color-fill-item:hover {
  cursor: pointer;
}

.color-fill-item:focus {
  box-shadow: none !important;
  outline: none !important;
  border-color: #ced4da !important;
}

::v-deep .multiselect__tags {
  min-height: 40px !important;
  border: 0 !important;
}

@media screen and (max-height: 730px) {
  .file-browser-wrapper {
    max-height: 400px;
  }
  
  .products-container {
    height: 500px;
  }

  .products-container--contracted {
    height: auto;
    width: 400px;
    padding: 5px 20px;
  }
  
}
.fields-module strong {
  font-size: 0.875rem !important;
  letter-spacing: 0.3px;
}
.green-fonts {
  color: #209c86 !important;
  margin-right: 5px;
}
.upload-button {
  border-radius: 0.35rem !important;
  border: 1px solid #e9ecef;
  padding-right: 0;
  padding-left: 10px;
  background-color: #209c86 !important;
  color: white;
  box-shadow: none !important;
}

.upload-button:hover {
  background-color: #209c86 !important;
}

.upload-button i {
  filter: drop-shadow(0 0 0 currentColor);
  margin-bottom: 12px !important;
}
.general-card-template {
  display: inline-block;
  margin: 8px;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease;
  position: relative;
  box-sizing: border-box;
  border-radius: 8px;
  overflow: hidden;
  background: #f4f4f4;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.image-card-template {
  width: 90px;
  height: 90px;
}
.image-card-template img {
  display: block;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  object-fit: contain; 
  transition: transform .25s ease;
}
.additional-card-template {
  margin: 8px;
  border: 2px dashed #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  box-sizing: border-box;
  border-radius: 8px;
  overflow: hidden;
  width: 90px;
  height: 90px;
}
.product-card-template {
  width: 65px;
  height: 65px;
}
.product-card-template img {
  display: block;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  object-fit: contain; 
  transition: transform .25s ease;
}
.image-loader {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}
img.is-loading {
  filter: blur(6px);
  opacity: 0.5;
}
.scroll-images-section{
  width: 100%;
  max-height: 222px;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  scrollbar-width: thin;
}
.colors-item  .no-right-border {
  border-right: none !important;
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}
.colors-item  .no-left-border {
  border-left: none !important;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
.image-multiselect{
  border: 1px solid #e9ecef !important;
  padding: 4px;
  background: white;
  border-radius: 6px;
}

.custom-color-picker {
  -webkit-appearance: none; /* safari support */
  -moz-appearance: none;    /* firefox support */
  appearance: none; 
  width: 42px !important;      
  height: 38px !important;   
  border-radius: 8px; 
  overflow: hidden;
  padding: 0;
  cursor: pointer;
}

.custom-color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.custom-color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 4px; 
}

.custom-color-picker::-moz-color-swatch {
  border: none;
  border-radius: 4px;
}

.color-fill-item:focus {
  border-color: none !important;
}

.custom-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 14px;
  color: #777;
}

.ms-bootstrap-size {
  font-size: var(--bs-body-font-size, 1rem);
  line-height: var(--bs-body-line-height, 1.5);
}

.ms-bootstrap-size {
  ::v-deep(.multiselect__placeholder),
  ::v-deep(.multiselect__input),
  ::v-deep(.multiselect__single),
  ::v-deep(.multiselect__tags) {
    font-size: inherit;
    line-height: inherit;
  }

  ::v-deep(.multiselect__input::placeholder) {
    font-size: inherit;
    line-height: inherit;
  }
}

.apply-to-section{
  bottom: 44px;
  z-index: 1;
}

</style>
